// api/aoAPI.js — Accounting Operations API client
import axiosInstance from './axiosConfig';

const aoAPI = {
  // ── Staff ──────────────────────────────────────────────────────────────────
  getStaff: async () => {
    try {
      const res = await axiosInstance.get('/ao/staff');
      return res.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to fetch staff';
    }
  },

  createStaff: async (data) => {
    try {
      const res = await axiosInstance.post('/ao/staff', data);
      return res.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to create staff';
    }
  },

  updateStaff: async (id, data) => {
    try {
      const res = await axiosInstance.put(`/ao/staff/${id}`, data);
      return res.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to update staff';
    }
  },

  deleteStaff: async (id) => {
    try {
      const res = await axiosInstance.delete(`/ao/staff/${id}`);
      return res.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to deactivate staff';
    }
  },

  // ── Clients ────────────────────────────────────────────────────────────────
  getClients: async (params = {}) => {
    try {
      const res = await axiosInstance.get('/ao/clients', { params });
      return res.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to fetch clients';
    }
  },

  createClient: async (data) => {
    try {
      const res = await axiosInstance.post('/ao/clients', data);
      return res.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to create client';
    }
  },

  updateClient: async (id, data) => {
    try {
      const res = await axiosInstance.put(`/ao/clients/${id}`, data);
      return res.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to update client';
    }
  },

  deleteClient: async (id) => {
    try {
      const res = await axiosInstance.delete(`/ao/clients/${id}`);
      return res.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to deactivate client';
    }
  },

  // ── Transactions ───────────────────────────────────────────────────────────
  getTransactions: async (clientId, params = {}) => {
    try {
      const res = await axiosInstance.get(`/ao/clients/${clientId}/transactions`, { params });
      return res.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to fetch transactions';
    }
  },

  upsertTransaction: async (clientId, data) => {
    try {
      const res = await axiosInstance.post(`/ao/clients/${clientId}/transactions`, data);
      return res.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to save transaction';
    }
  },

  updateTransaction: async (id, data) => {
    try {
      const res = await axiosInstance.put(`/ao/transactions/${id}`, data);
      return res.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to update transaction';
    }
  },

  deleteTransaction: async (id) => {
    try {
      const res = await axiosInstance.delete(`/ao/transactions/${id}`);
      return res.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to delete transaction';
    }
  },

  // ── Reports ────────────────────────────────────────────────────────────────
  getWorkingPaper: async (params = {}) => {
    try {
      const res = await axiosInstance.get('/ao/report/working-paper', { params });
      return res.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to fetch working paper report';
    }
  },

  getCollectionStatus: async (params = {}) => {
    try {
      const res = await axiosInstance.get('/ao/report/collection-status', { params });
      return res.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to fetch collection status';
    }
  },

  // ── Recurring PF ──────────────────────────────────────────────────────────
  getRecurringPreview: async (params = {}) => {
    try {
      const res = await axiosInstance.get('/ao/recurring/preview', { params });
      return res.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to fetch recurring preview';
    }
  },

  generateRecurring: async (payload = {}) => {
    try {
      const res = await axiosInstance.post('/ao/recurring/generate', payload);
      return res.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to generate recurring transactions';
    }
  },

  updateClientRecurring: async (clientId, payload) => {
    try {
      const res = await axiosInstance.put(`/ao/clients/${clientId}`, payload);
      return res.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to update client recurring settings';
    }
  },

  // ── Disbursements ─────────────────────────────────────────────────────────
  getDisbursements: async (params = {}) => {
    try {
      const res = await axiosInstance.get('/ao/disbursements', { params });
      return res.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to fetch disbursements';
    }
  },

  createDisbursement: async (data) => {
    try {
      const res = await axiosInstance.post('/ao/disbursements', data);
      return res.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to create disbursement';
    }
  },

  updateDisbursement: async (id, data) => {
    try {
      const res = await axiosInstance.patch(`/ao/disbursements/${id}`, data);
      return res.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to update disbursement';
    }
  },

  deleteDisbursement: async (id) => {
    try {
      const res = await axiosInstance.delete(`/ao/disbursements/${id}`);
      return res.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to delete disbursement';
    }
  },

  // ── Collections ────────────────────────────────────────────────────────────
  getCollections: async (params = {}) => {
    try {
      const res = await axiosInstance.get('/ao/collections', { params });
      return res.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to fetch collections';
    }
  },

  createCollection: async (data) => {
    try {
      const res = await axiosInstance.post('/ao/collections', data);
      return res.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to create collection';
    }
  },

  updateCollection: async (id, data) => {
    try {
      const res = await axiosInstance.patch(`/ao/collections/${id}`, data);
      return res.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to update collection';
    }
  },

  deleteCollection: async (id) => {
    try {
      const res = await axiosInstance.delete(`/ao/collections/${id}`);
      return res.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to delete collection';
    }
  },

  getDailyReport: async (params = {}) => {
    try {
      const res = await axiosInstance.get('/ao/collections/report/daily', { params });
      return res.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to fetch daily report';
    }
  },

  // ── AR Summary ─────────────────────────────────────────────────────────────
  getARSummary: async (params = {}) => {
    try {
      const res = await axiosInstance.get('/ao/disbursements/summary/ar', { params });
      return res.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to fetch AR summary';
    }
  },

  // ── Daily Expenses Report ──────────────────────────────────────────────────
  getDailyExpensesReport: async (params = {}) => {
    try {
      const res = await axiosInstance.get('/ao/reports/daily-expenses', { params });
      return res.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to fetch daily expenses report';
    }
  },

  // ── Land Titles ────────────────────────────────────────────────────────────
  getLandTitles: async (params = {}) => {
    try {
      const res = await axiosInstance.get('/ao/land-titles', { params });
      return res.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to fetch land titles';
    }
  },

  createLandTitle: async (data) => {
    try {
      const res = await axiosInstance.post('/ao/land-titles', data);
      return res.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to create land title';
    }
  },

  updateLandTitle: async (id, data) => {
    try {
      const res = await axiosInstance.patch(`/ao/land-titles/${id}`, data);
      return res.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to update land title';
    }
  },

  deleteLandTitle: async (id) => {
    try {
      const res = await axiosInstance.delete(`/ao/land-titles/${id}`);
      return res.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to delete land title';
    }
  },

  getLandTitleExpenses: async (id) => {
    try {
      const res = await axiosInstance.get(`/ao/land-titles/${id}/expenses`);
      return res.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to fetch land title expenses';
    }
  },

  // ── Petty Cash ─────────────────────────────────────────────────────────────
  getPettyCash: async (params = {}) => {
    try {
      const res = await axiosInstance.get('/ao/petty-cash', { params });
      return res.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to fetch petty cash';
    }
  },

  createPettyCash: async (data) => {
    try {
      const res = await axiosInstance.post('/ao/petty-cash', data);
      return res.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to create petty cash advance';
    }
  },

  updatePettyCash: async (id, data) => {
    try {
      const res = await axiosInstance.patch(`/ao/petty-cash/${id}`, data);
      return res.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to update petty cash';
    }
  },

  deletePettyCash: async (id) => {
    try {
      const res = await axiosInstance.delete(`/ao/petty-cash/${id}`);
      return res.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to delete petty cash';
    }
  },

  getPettyCashLedger: async (params = {}) => {
    try {
      const res = await axiosInstance.get('/ao/petty-cash/reports/ledger', { params });
      return res.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to fetch petty cash ledger';
    }
  },
};

export default aoAPI;
